import { reactive, onUnmounted } from 'vue';

const MIN_DRAG_DISTANCE = 4; // px — evita drag acidental em cliques simples

export function useMeetingDayDrag({ dayCellRefs, emit }) {
  const dragState = reactive({
    draggingId: null,
    targetDayKey: null,
  });

  let activeDrag = null;
  let clone = null;

  function getTargetCell(clientX, clientY) {
    const cells = dayCellRefs.value;
    let result = null;
    cells.forEach((el, dayKey) => {
      if (!el || result) return;
      const rect = el.getBoundingClientRect();
      if (
        clientX >= rect.left &&
        clientX <= rect.right &&
        clientY >= rect.top &&
        clientY <= rect.bottom
      ) {
        result = dayKey;
      }
    });
    return result;
  }

  function createClone(meeting, event, cardEl) {
    const rect = cardEl.getBoundingClientRect();

    const el = document.createElement('div');
    el.className = [
      'fixed',
      'z-[9999]',
      'pointer-events-none',
      'rounded',
      'px-1',
      'text-xs',
      'shadow-lg',
      'opacity-90',
      'text-white',
      'truncate',
    ].join(' ');
    el.style.backgroundColor = meeting.user?.color ?? '';
    el.style.width = `${rect.width}px`;
    el.style.height = `${rect.height}px`;
    el.style.left = `${event.clientX - activeDrag.offsetX}px`;
    el.style.top = `${event.clientY - activeDrag.offsetY}px`;
    el.textContent = meeting.title;

    document.body.appendChild(el);

    return el;
  }

  function onMousemove(event) {
    if (!activeDrag) return;

    const { startX, startY } = activeDrag;
    const moved =
      Math.abs(event.clientX - startX) > MIN_DRAG_DISTANCE ||
      Math.abs(event.clientY - startY) > MIN_DRAG_DISTANCE;

    if (!moved && !activeDrag.started) return;

    if (!activeDrag.started) {
      activeDrag.started = true;
      dragState.draggingId = activeDrag.meeting.id;
      document.body.style.cursor = 'grabbing';
      document.body.style.userSelect = 'none';
      clone = createClone(activeDrag.meeting, event, activeDrag.cardEl);
    }

    clone.style.left = `${event.clientX - activeDrag.offsetX}px`;
    clone.style.top = `${event.clientY - activeDrag.offsetY}px`;

    const targetDayKey = getTargetCell(event.clientX, event.clientY);
    activeDrag.targetDayKey = targetDayKey;
    dragState.targetDayKey = targetDayKey;
  }

  function onMouseup() {
    if (!activeDrag) return;

    document.removeEventListener('mousemove', onMousemove);
    document.removeEventListener('mouseup', onMouseup);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';

    if (clone) {
      clone.remove();
      clone = null;
    }

    dragState.draggingId = null;
    dragState.targetDayKey = null;

    if (!activeDrag.started) {
      activeDrag = null;
      return;
    }

    const { meeting, targetDayKey } = activeDrag;
    activeDrag = null;

    if (!targetDayKey) return;

    const originalStart = new Date(meeting.start_at);
    const originalEnd = new Date(meeting.end_at);
    const duration = originalEnd - originalStart;

    const [year, month, day] = targetDayKey.split('-').map(Number);
    const newStart = new Date(originalStart);
    newStart.setFullYear(year, month - 1, day);
    const newEnd = new Date(newStart.getTime() + duration);

    if (
      newStart.getTime() === originalStart.getTime() &&
      newEnd.getTime() === originalEnd.getTime()
    ) {
      return;
    }

    emit('meetingDrop', {
      id: meeting.id,
      start_at: newStart.toISOString(),
      end_at: newEnd.toISOString(),
      originalMeeting: meeting,
    });
  }

  function onMeetingMousedown(event, meeting, cardEl) {
    if (activeDrag) {
      document.removeEventListener('mousemove', onMousemove);
      document.removeEventListener('mouseup', onMouseup);
      if (clone) {
        clone.remove();
        clone = null;
      }
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      dragState.draggingId = null;
      dragState.targetDayKey = null;
      activeDrag = null;
    }

    event.preventDefault();

    const rect = cardEl.getBoundingClientRect();
    activeDrag = {
      meeting,
      cardEl,
      startX: event.clientX,
      startY: event.clientY,
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top,
      started: false,
      targetDayKey: null,
    };

    document.addEventListener('mousemove', onMousemove);
    document.addEventListener('mouseup', onMouseup);
  }

  onUnmounted(() => {
    document.removeEventListener('mousemove', onMousemove);
    document.removeEventListener('mouseup', onMouseup);
    if (clone) {
      clone.remove();
      clone = null;
    }
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    activeDrag = null;
  });

  return { onMeetingMousedown, dragState };
}
